/**
 * Shared phone utilities used across the app.
 *
 * Stored format: "+<countryCode>-<digits>"  e.g. "+92-3001234567"
 */

/**
 * Countries where the local dialling format starts with a trunk prefix "0"
 * that must be dropped in international format.
 * e.g. Pakistan: local 0300-1234567 → international +92-3001234567
 */
const STRIP_LEADING_ZERO = new Set([
  '92',  // Pakistan
  '91',  // India
  '44',  // UK
  '971', // UAE
  '966', // Saudi Arabia
  '90',  // Turkey
  '61',  // Australia
  '49',  // Germany
  '33',  // France
  '39',  // Italy
]);

/**
 * National Subscriber Number (NSN) length per country code, after stripping
 * the trunk-zero. Used to enforce input length and validation.
 *
 * Sources: ITU-T E.164 country plans. Where a country uses variable-length
 * numbers, we accept the full known range (e.g. Germany 10–11 digits).
 */
const PHONE_LENGTH = {
  '92':  { min: 10, max: 10 }, // Pakistan
  '971': { min: 9,  max: 9  }, // UAE
  '966': { min: 9,  max: 9  }, // Saudi Arabia
  '44':  { min: 10, max: 10 }, // United Kingdom
  '1':   { min: 10, max: 10 }, // USA / Canada
  '974': { min: 8,  max: 8  }, // Qatar
  '968': { min: 8,  max: 8  }, // Oman
  '965': { min: 8,  max: 8  }, // Kuwait
  '973': { min: 8,  max: 8  }, // Bahrain
  '61':  { min: 9,  max: 9  }, // Australia
  '49':  { min: 10, max: 11 }, // Germany
  '60':  { min: 9,  max: 10 }, // Malaysia
  '91':  { min: 10, max: 10 }, // India
  '86':  { min: 11, max: 11 }, // China
};

const DEFAULT_LENGTH = { min: 7, max: 15 }; // E.164 fallback

/** Get the allowed NSN length range for a country code. */
export const getPhoneLength = (countryCode) =>
  PHONE_LENGTH[countryCode] || DEFAULT_LENGTH;

/** Build the stored "+<code>-<digits>" format from parts. */
export const buildPhone = (countryCode, localDigits) => {
  const { max } = getPhoneLength(countryCode);
  let digits = (localDigits || '').replace(/\D/g, '');

  // Auto-strip leading 0 (trunk prefix) for countries that use it
  if (STRIP_LEADING_ZERO.has(countryCode) && digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  digits = digits.slice(0, max);
  if (!digits) return '';

  return `+${countryCode}-${digits}`;
};

/** Parse stored phone into { countryCode, localDigits }. */
export const parsePhone = (fullPhone, defaultCode = '92') => {
  if (!fullPhone) return { countryCode: defaultCode, localDigits: '' };
  const m = fullPhone.match(/^\+(\d{1,4})-?(.*)$/);
  if (!m) return { countryCode: defaultCode, localDigits: (fullPhone || '').replace(/\D/g, '') };
  return { countryCode: m[1], localDigits: (m[2] || '').replace(/\D/g, '') };
};

/**
 * Validate a phone in stored "+<code>-<digits>" format against the
 * country's allowed NSN length range.
 */
export const validatePhone = (phone) => {
  if (!phone) return false;
  const match = phone.match(/^\+(\d{1,4})-(.*)$/);
  if (!match) return false;
  const countryCode = match[1];
  const localDigits = match[2].replace(/\D/g, '');
  const { min, max } = getPhoneLength(countryCode);
  return localDigits.length >= min && localDigits.length <= max;
};
